import {
  customerTermsAdjustmentSchema,
  customerTermsAdjustmentUpdateSchema,
} from '@snap-types/models/customer-terms-adjustment';
import {
  lastGrossMarginAdjustmentSchema,
  lastGrossMarginAdjustmentUpdateSchema,
} from '@snap-types/models/last-gross-margin-adjustment';
import {
  lastTransactionAgeAdjustmentSchema,
  lastTransactionAgeAdjustmentUpdateSchema,
} from '@snap-types/models/last-transaction-age-adjustment';
import { marketIndustryAdjustmentSchema } from '@snap-types/models/market-industry-adjustment';
import { z } from 'zod/v4';

import {
  columns,
  customerPortalsColumns,
  customerPotentialColumns,
  customerTermsColumns,
  lastGrossMarginColumns,
  lastTransactionAgeColumns,
  marketIndustryColumns,
  productComplexityColumns,
  productOperationsCountColumns,
} from './columns';

export const priceSegmentConfigSchema = z.object(columns);

export const priceSegmentConfigWithRelationsSchema = z.discriminatedUnion(
  'priceSegmentType',
  [
    z.object({ ...columns, ...customerPotentialColumns.shape }),
    z.object({ ...columns, ...customerPortalsColumns.shape }),
    z.object({ ...columns, ...productOperationsCountColumns.shape }),
    z.object({ ...columns, ...productComplexityColumns.shape }),
    z.object({
      ...columns,
      ...customerTermsColumns.shape,
      customerTermsAdjustments: z.array(customerTermsAdjustmentSchema),
    }),
    z.object({
      ...columns,
      ...marketIndustryColumns.shape,
      marketIndustryAdjustments: z.array(marketIndustryAdjustmentSchema),
    }),
    z.object({
      ...columns,
      ...lastTransactionAgeColumns.shape,
      lastTransactionAgeAdjustments: z.array(
        lastTransactionAgeAdjustmentSchema,
      ),
    }),
    z.object({
      ...columns,
      ...lastGrossMarginColumns.shape,
      lastGrossMarginAdjustments: z.array(lastGrossMarginAdjustmentSchema),
    }),
  ],
);

export type PriceSegmentConfigWithRelations = z.output<
  typeof priceSegmentConfigWithRelationsSchema
>;

const priceSegmentConfigUpdateBaseSchema = priceSegmentConfigSchema.pick({
  sortOrder: true,
  enabled: true,
});

export const priceSegmentConfigUpdateSchema = z.discriminatedUnion(
  'priceSegmentType',
  [
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...customerPotentialColumns.shape,
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...customerPortalsColumns.shape,
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...customerTermsColumns.shape,
      customerTermsAdjustments: z.array(
        customerTermsAdjustmentUpdateSchema.pick({
          netDays: true,
          adjustment: true,
        }),
      ),
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...productOperationsCountColumns.shape,
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...productComplexityColumns.shape,
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...marketIndustryColumns.shape,
      marketIndustryAdjustments: z.array(
        marketIndustryAdjustmentSchema.pick({
          adjustment: true,
          marketCode: true,
        }),
      ),
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...lastTransactionAgeColumns.shape,
      lastTransactionAgeAdjustments: z.array(
        lastTransactionAgeAdjustmentUpdateSchema.pick({
          ageInDays: true,
          adjustment: true,
        }),
      ),
    }),
    z.object({
      ...priceSegmentConfigUpdateBaseSchema.shape,
      ...lastGrossMarginColumns.shape,
      lastGrossMarginAdjustments: z.array(
        lastGrossMarginAdjustmentUpdateSchema.pick({
          margin: true,
          adjustment: true,
        }),
      ),
    }),
  ],
);

export type PriceSegmentConfig = z.output<typeof priceSegmentConfigSchema>;

export type PriceSegmentConfigUpdate = z.output<
  typeof priceSegmentConfigUpdateSchema
>;
